/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';

export default class TopicController extends Controller {
  @service
  session;

  @service
  options;

  @service
  strings;

  @service
  features;

  @tracked
  printDialog = false;

  @optionAlias('hero:lock:milestones')
  milestonesLocked;

  @alias('features.sidekickEnabled')
  sidekickEnabled;

  @tracked
  topicTab = 'detail';

  @action
  setTopicTab(value) {
    this.topicTab = value;
  }

  @computed('model.currentGoal.goal.linkedGoals.content.[]')
  get sortedCoreGoals() {
    let linkedGoals = this.get('model.currentGoal.goal.linkedGoals') || [];
    return this.groupGoalsBySubcategory(linkedGoals);
  }

  @computed('model.currentGoal.goal.priorLinkedGoals.content.[]')
  get sortedPriorGoals() {
    let linkedGoals = this.get('model.currentGoal.goal.priorLinkedGoals') || [];
    return this.groupGoalsBySubcategory(linkedGoals);
  }

  @computed('model.currentGoal.goal.futureLinkedGoals.content.[]')
  get sortedFutureGoals() {
    let linkedGoals =
      this.get('model.currentGoal.goal.futureLinkedGoals') || [];
    return this.groupGoalsBySubcategory(linkedGoals);
  }

  // Helper method to group goals by subcategory label
  groupGoalsBySubcategory(goals) {
    // ensure goals are resolved upfront.
    return resolved(goals).reduce((acc, goal) => {
      let subcategoryLabel = goal.get('subcategory.label') || 'Other';
      let subject = goal.get('subject');
      let subjectType = subject.get('subjectTypeName') || 'Goals';
      let subjectPriority = subject.get('subjectTypePriority') || 0;

      // Store subject types with priority for sorting but exclude it later
      let sortingKey = `${subjectPriority}-${subjectType}`;

      if (!acc[sortingKey]) {
        acc[sortingKey] = {};
      }
      if (!acc[sortingKey][subcategoryLabel]) {
        acc[sortingKey][subcategoryLabel] = [];
      }

      // Initialize total count if it doesn't exist
      if (!acc.totalCount) {
        acc.totalCount = 0;
      }

      // Add the goal to the grouped structure
      acc[sortingKey][subcategoryLabel].push(goal);
      acc.totalCount += 1;

      return acc;
    }, {});
  }

  @computed('sortedCoreGoals', 'sortedPriorGoals', 'sortedFutureGoals')
  get combinedGoals() {
    let combinedGoals = {};

    // Collect and sort subjects, excluding `totalCount`
    let allSubjects = [
      ...Object.keys(this.sortedCoreGoals || {}).filter(
        (key) => key !== 'totalCount',
      ),
      ...Object.keys(this.sortedPriorGoals || {}).filter(
        (key) => key !== 'totalCount',
      ),
      ...Object.keys(this.sortedFutureGoals || {}).filter(
        (key) => key !== 'totalCount',
      ),
    ];

    // Sort by numeric priority (extracted from "priority-subjectType")
    allSubjects.sort((a, b) => {
      let [priorityA] = a.split('-').map(Number);
      let [priorityB] = b.split('-').map(Number);
      return priorityA - priorityB;
    });

    allSubjects.forEach((subjectWithPriority) => {
      let [, subject] = subjectWithPriority.split('-'); // Extract subject type without priority

      let coreGoalsBySubject =
        (this.sortedCoreGoals && this.sortedCoreGoals[subjectWithPriority]) ||
        {};
      let priorGoalsBySubject =
        (this.sortedPriorGoals && this.sortedPriorGoals[subjectWithPriority]) ||
        {};
      let futureGoalsBySubject =
        (this.sortedFutureGoals &&
          this.sortedFutureGoals[subjectWithPriority]) ||
        {};

      // Initialize subject entry without priority
      combinedGoals[subject] = {};

      // Collect subcategories within the current subject, excluding `totalCount`
      let allSubcategories = new Set([
        ...Object.keys(coreGoalsBySubject).filter(
          (key) => key !== 'totalCount',
        ),
        ...Object.keys(priorGoalsBySubject).filter(
          (key) => key !== 'totalCount',
        ),
        ...Object.keys(futureGoalsBySubject).filter(
          (key) => key !== 'totalCount',
        ),
      ]);

      allSubcategories.forEach((subcategory) => {
        combinedGoals[subject][subcategory] = {
          priorGoals: priorGoalsBySubject[subcategory] || [],
          coreGoals: coreGoalsBySubject[subcategory] || [],
          futureGoals: futureGoalsBySubject[subcategory] || [],
        };
      });
    });

    return combinedGoals;
  }

  @action
  openPrintDialog() {
    this.printDialog = true;
  }

  @action
  closePrintDialog() {
    this.printDialog = false;
  }
}
